"use client";
import type React from "react";
import { useEffect, useState } from "react";
import ProductAttributesBuilder from "./ProductAttributesBuilder";
import {
  attrsInitValues,
  getIndexOfChild,
  getUnavailableAttributes,
} from "@/app/components/common-addons-builder/addon-functions";
import Separator from "@/app/components/lib/Separator";
import AttributesStepsSelector from "./AttributesStepsSelector";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { AttributesSelectionMethods } from "@/api/AppApi/services/company/interfaces/ICompany";
import {
  fillSelectedAttributes,
  findUnavailableAttributes,
  findOutOfStockAttributes,
} from "./helpers/functions";
import type { IAttiribs, IProductMinimizedData } from "./helpers/interfaces";
import { useGetCartQuery } from "@/api/AppApi/services/cart";

interface Props {
  product: IProductMinimizedData;
  setSelectedChildIndex: (index: number | null) => void;
  initialValues?: number[];
  isTopWinner?: boolean;
  autoSelectFirstChild: boolean;
  detailsCountaineMaxWidth?: number;
  isProductBox?: boolean;
  setSelectedAttributesValues?: (selectedAttributes: number[]) => void;
  noStepper?: boolean;
  showAllAttributes?: boolean;
  allow_user_request_stock_availability_reminder?: boolean;
}
const Attributes: React.FC<Props> = ({
  product,
  setSelectedChildIndex,
  initialValues,
  isTopWinner,
  autoSelectFirstChild,
  detailsCountaineMaxWidth,
  isProductBox,
  setSelectedAttributesValues,
  noStepper,
  showAllAttributes,
  allow_user_request_stock_availability_reminder,
}) => {
  const getCart = useGetCartQuery();
  const cartItems = getCart?.data?.result?.items;
  const [selectedAttributes, setSelectedAttributes] = useState<IAttiribs>({});
  const [unavailabeAttributes, setUnavailabeAttributes] = useState<IAttiribs>(
    {},
  );
  const [outOfStockAttributes, setOutOfStockAttributes] = useState<IAttiribs>(
    {},
  );
  const [currentAttributeToSelectIdx, setCurrentAttributeToSelectIdx] =
    useState(
      () =>
        fillSelectedAttributes(
          product.attributes_builder,
          0,
          unavailabeAttributes,
        ).currentIndex,
    );
  const getCompany = useGetCompanyQuery();
  const company = getCompany?.data?.result;
  const { attributes_selection_methods } =
    company?.theme?.components?.product ?? {};
  const isSteper =
    attributes_selection_methods === AttributesSelectionMethods.cascading; // TODO need edit after backend finish
  useEffect(() => {
    if (
      !product ||
      product?.children?.length === 0 ||
      product?.attributes_builder?.length === 0
    )
      return;
    console.log(product);
    const selected: any = attrsInitValues(
      product.attributes_builder,
      initialValues,
    );
    let unAvailable = attrsInitValues(product.attributes_builder, []);
    if (initialValues) {
      for (const key of Object.keys(selected)) {
        getUnavailableAttributes(key, selected[key], product);
        unAvailable = {
          ...unAvailable,
          ...getUnavailableAttributes(key, selected[key], product),
        };
      }
    }
    const attrsWithSelectFirstChild = product?.attributes_builder?.filter(
      (attr) =>
        autoSelectFirstChild ||
        (attr?.data?.length === 1 &&
          !(unAvailable as IAttiribs)[attr.configurations.key].includes(
            attr.data?.[0]?.key,
          )),
    );
    const selectdAttersWithFirstOption: IAttiribs = {
      ...attrsWithSelectFirstChild.reduce((acc: IAttiribs, currentValue) => {
        acc[currentValue?.configurations?.key] = [
          currentValue.data?.find(
            (at) =>
              !(findUnavailableAttributes(acc, product) as IAttiribs)?.[
                currentValue.configurations.key
              ]?.includes(at.key),
          )?.key!,
        ];
        return acc;
      }, {}),
    };
    for (const property in selectdAttersWithFirstOption) {
      if (selectdAttersWithFirstOption[property]?.[0] === undefined) {
        selectdAttersWithFirstOption[property] = [];
      }
    }
    const selectedAttrs = { ...selected, ...selectdAttersWithFirstOption };
    const index = getIndexOfChild(product?.children, selectedAttrs); //null,-1 or index of child
    unAvailable = findUnavailableAttributes(selectedAttrs, product);
    const outOfStock = findOutOfStockAttributes(
      selectedAttrs,
      product,
      cartItems,
    );

    setSelectedAttributes(selectedAttrs);
    setUnavailabeAttributes({ ...unAvailable });
    setOutOfStockAttributes({ ...outOfStock });
    setSelectedChildIndex(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleAttributesChanged = (
    key: string,
    attrib: string[],
    clearAhead = false,
    isautoSelectFirstChild = false,
  ) => {
    const newState = { ...selectedAttributes, [key]: attrib };
    const currentAttr = product.attributes_builder.findIndex(
      (attrib) => attrib.configurations.key === key,
    );
    if (clearAhead) {
      product.attributes_builder
        .filter((_, idx) => idx > currentAttr)
        .forEach((item) => (newState[item.configurations.key] = []));
    }
    if (isautoSelectFirstChild) {
      product.attributes_builder
        .filter((_, idx) => idx > currentAttr)
        .forEach((item) => {
          newState[item.configurations.key] = [
            item?.data?.find(
              (at) =>
                !(findUnavailableAttributes(newState, product) as IAttiribs)?.[
                  item.configurations.key
                ].includes(at.key),
            )?.key!,
          ];
          for (const property in newState) {
            if (
              newState[property]?.[0] === undefined ||
              newState[property]?.find((value) => value === undefined)
            ) {
              newState[property] = [];
            }
          }
        });
    }
    setSelectedAttributes(newState);
    const unavailable = findUnavailableAttributes(newState, product);
    const outOfStock = findOutOfStockAttributes(newState, product, cartItems);

    setUnavailabeAttributes({ ...unavailable });
    setOutOfStockAttributes({ ...outOfStock });
    const index = getIndexOfChild(product?.children, newState); //null,-1 or index of child
    setSelectedChildIndex(index);
  };

  useEffect(() => {
    if (setSelectedAttributesValues)
      setSelectedAttributesValues?.(
        Object.values(selectedAttributes)
          .flat()
          .map((item) => Number(item)),
      );
  }, [selectedAttributes, setSelectedAttributesValues]);
  if (isSteper && !autoSelectFirstChild && !isProductBox && !noStepper)
    return (
      <AttributesStepsSelector
        {...product}
        attributesMaxWidth={detailsCountaineMaxWidth}
        currentAttributeToSelectIdx={currentAttributeToSelectIdx}
        setCurrentAttributeToSelectIdx={setCurrentAttributeToSelectIdx}
        selectedAttributes={selectedAttributes}
        handleAttributesChanged={handleAttributesChanged}
        unAvailableKeys={unavailabeAttributes}
        outOfStockKeys={outOfStockAttributes}
        allow_user_request_stock_availability_reminder={
          allow_user_request_stock_availability_reminder
        }
      />
    );
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: isProductBox ? 8 : 24,
      }}
    >
      {product?.attributes_builder
        .filter((attr) => attr?.data?.length && attr?.data?.length > 1)
        .filter((_attr, idx) =>
          isProductBox && !showAllAttributes ? idx < 1 : true,
        )
        .map((attribute) => {
          return (
            <div key={attribute.configurations.key}>
              {!isTopWinner &&
                !isProductBox &&
                attribute?.data?.length! > 1 && (
                  <Separator
                    startLabel={attribute.configurations.title}
                    marginBottom={8}
                  />
                )}
              {isProductBox && (
                <div className="start-from">
                  {attribute.configurations.title}
                </div>
              )}
              <ProductAttributesBuilder
                attributesMaxWidth={detailsCountaineMaxWidth}
                productChildren={product?.children!}
                attribute={attribute}
                unAvailabeKeys={
                  unavailabeAttributes[attribute.configurations.key]
                }
                outOfStockKeys={
                  outOfStockAttributes[attribute.configurations.key]
                }
                state={selectedAttributes[attribute.configurations.key]}
                setState={(attrib) =>
                  handleAttributesChanged(
                    attribute.configurations.key,
                    attrib,
                    autoSelectFirstChild,
                    autoSelectFirstChild,
                  )
                }
                isProductBox={isProductBox}
                allow_user_request_stock_availability_reminder={
                  allow_user_request_stock_availability_reminder
                }
              />
            </div>
          );
        })}
    </div>
  );
};

export default Attributes;
